import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import type { Profile } from '../types/database';

interface AuthState {
  // State
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
  error: AuthError | null;

  // Actions
  initialize: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  resetPasswordForEmail: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  resendVerificationEmail: (email: string) => Promise<{ error: AuthError | null }>;
  clearError: () => void;
}

/**
 * Fetch the profile row for a user and file it into the store on arrival.
 *
 * Deliberately fire-and-forget: nothing that answers "is this person signed
 * in?" may block on it. A response that arrives after the user has changed or
 * signed out is dropped rather than overwriting the newer state.
 */
let profileFetchInFlightFor: string | null = null;

const fetchProfile = async (userId: string): Promise<void> => {
  if (!supabase) return;
  // A token refresh arriving mid-fetch must not start a second identical
  // query; the one in flight will answer it.
  if (profileFetchInFlightFor === userId) return;
  profileFetchInFlightFor = userId;

  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (useAuthStore.getState().user?.id !== userId) return;

    useAuthStore.setState({ profile: (profile as Profile | null) ?? null });
  } finally {
    if (profileFetchInFlightFor === userId) profileFetchInFlightFor = null;
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  session: null,
  loading: true,
  initialized: false,
  error: null,

  initialize: async () => {
    // A fresh initialize is a fresh session — drop any stale in-flight marker.
    profileFetchInFlightFor = null;

    // If Supabase is not configured, skip initialization
    if (!isSupabaseConfigured() || !supabase) {
      set({ loading: false, initialized: true });
      return;
    }

    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      // Publish the session before the profile round-trip. "Is someone
      // signed in?" is the question the header, the dashboard and the data
      // layer all ask, and it must not wait on the slower "who are they in
      // detail?" query (245b39a5).
      set({
        user: session?.user ?? null,
        session: session ?? null,
        loading: false,
        initialized: true,
      });

      if (session?.user) {
        void fetchProfile(session.user.id);
      }

      // Listen for auth changes
      supabase!.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') {
          profileFetchInFlightFor = null;
          set({ user: null, session: null, profile: null });
          return;
        }

        // initialize() above owns the first read; INITIAL_SESSION would only
        // duplicate it (and its profile fetch).
        if (event === 'INITIAL_SESSION') return;

        if (!session?.user) return;

        const previousUserId = get().user?.id;

        // Same tick as Supabase hands us the token — no await in front of it.
        // TOKEN_REFRESHED lands here too, so the stored session never goes
        // stale behind Supabase's own copy.
        set({ user: session.user, session });

        if (previousUserId !== session.user.id || !get().profile) {
          void fetchProfile(session.user.id);
        }
      });
    } catch (error) {
      console.error('Auth initialization error:', error);
      set({ loading: false, initialized: true, error: error as AuthError });
    }
  },

  signInWithEmail: async (email, password) => {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: 'Cloud features not configured. Please add Supabase credentials.' } as AuthError;
      set({ error });
      return { error };
    }

    set({ loading: true, error: null });

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    set({ loading: false, error });
    return { error };
  },

  signUpWithEmail: async (_email, _password, _displayName) => {
    // Public sign-up is closed. No UI path reaches this function anymore
    // (AuthModal no longer offers a sign-up mode), but it is kept defensive
    // here too and never calls supabase.auth.signUp.
    const error = { message: 'Neume is not open for public sign-up.' } as AuthError;
    set({ error });
    return { error };
  },

  signInWithGoogle: async () => {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: 'Cloud features not configured. Please add Supabase credentials.' } as AuthError;
      set({ error });
      return { error };
    }

    set({ loading: true, error: null });

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });

    set({ loading: false, error });
    return { error };
  },

  signOut: async () => {
    if (!isSupabaseConfigured() || !supabase) {
      set({ user: null, session: null, profile: null });
      return;
    }

    set({ loading: true });
    await supabase.auth.signOut();
    set({ user: null, session: null, profile: null, loading: false });
  },

  updateProfile: async (updates) => {
    if (!isSupabaseConfigured() || !supabase) {
      return { error: new Error('Cloud features not configured') };
    }

    const { user } = get();
    if (!user) return { error: new Error('Not authenticated') };

    // Extract only the updatable fields
    const updateData: { email?: string | null; display_name?: string | null; avatar_url?: string | null } = {};
    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.display_name !== undefined) updateData.display_name = updates.display_name;
    if (updates.avatar_url !== undefined) updateData.avatar_url = updates.avatar_url;

    const { error } = await supabase
      .from('profiles')
      .update(updateData as never)
      .eq('id', user.id);

    if (!error) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      set({ profile });
    }

    return { error };
  },

  resetPasswordForEmail: async (email) => {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: 'Cloud features not configured. Please add Supabase credentials.' } as AuthError;
      set({ error });
      return { error };
    }

    set({ loading: true, error: null });

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });

    set({ loading: false, error });
    return { error };
  },

  updatePassword: async (newPassword) => {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: 'Cloud features not configured. Please add Supabase credentials.' } as AuthError;
      set({ error });
      return { error };
    }

    set({ loading: true, error: null });

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    set({ loading: false, error });
    return { error };
  },

  resendVerificationEmail: async (email) => {
    if (!isSupabaseConfigured() || !supabase) {
      const error = { message: 'Cloud features not configured. Please add Supabase credentials.' } as AuthError;
      set({ error });
      return { error };
    }

    set({ loading: true, error: null });

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });

    set({ loading: false, error });
    return { error };
  },

  clearError: () => set({ error: null }),
}));

/**
 * The one identity read for code that lives outside React.
 *
 * Services used to ask Supabase directly (`supabase.auth.getSession()`), which
 * is a *second* source of truth about who is signed in: Supabase knows the
 * moment the token lands, while the store used to wait on a profile query
 * behind it. The dashboard's data therefore went live while the header still
 * showed "Sign In" (245b39a5). Everything that needs an identity now reads the
 * store, so every surface flips together.
 *
 * The one exception is the fallback below: before `initialize()` has settled
 * there is nothing in the store yet, and reporting "signed out" would silently
 * push a signed-in user onto localStorage. Supabase's own session is the right
 * answer for that window — it is what the store is about to read anyway.
 *
 * Note this returns an identity, never a bearer token. Code that needs an
 * `access_token` for a request must keep calling `supabase.auth.getSession()`,
 * which owns transparent refresh; a token copied out of the store can be
 * expired.
 */
export async function getCurrentUserId(): Promise<string | null> {
  if (!isSupabaseConfigured() || !supabase) return null;

  const { session, initialized } = useAuthStore.getState();
  if (initialized) return session?.user?.id ?? null;

  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}
